import { Controller, useFormContext } from 'react-hook-form'
import { MenuItem, Typography, IconButton, InputAdornment } from '@mui/material'
import CustomTextField from '@core/components/mui/TextField'
import Chip from '@mui/material/Chip'
import { useTranslator } from '@/hooks'
import React from 'react'

type Option = {
  value: string | number
  label: string
  icon?: React.ReactNode
}

type FormSelectProps = {
  name?: string
  labelText?: string
  placeholder?: string
  required?: boolean
  disabledYn?: boolean
  options?: Option[]
  className?: string
  slotProps?: any
  multiple?: boolean
  disabled?: boolean
  defaultValue?: string | string[]
  onClear?: () => void
  height?: string
  onChange?: (value: any, option?: any) => void
  forceLabelFromOptions?: boolean
}

const FormSelect = ({
  name = '',
  labelText = '',
  placeholder = '',
  required = false,
  disabledYn = false,
  options = [],
  className = '',
  slotProps = {},
  multiple = false,
  defaultValue,
  onClear,
  height = '48px',
  onChange,
  forceLabelFromOptions = false,
  ...props
}: FormSelectProps & { value?: any; onChange?: (value: any, option?: any) => void }) => {
  const {
    control,
    formState: { errors: _errors },
    setValue
  } = useFormContext()

  const sxProps = slotProps.sx || {}
  const selectProps = slotProps.select || {}
  const { __ } = useTranslator()

  return (
    <div className={className}>
      <Controller
        name={name}
        control={control}
        rules={{ required }}
        render={({ field, fieldState }) => {
          const currentValue = multiple ? field.value || [] : (field.value ?? '')

          return (
            <CustomTextField
              select
              value={currentValue}
              onChange={e => {
                field.onChange(e)
                const newValue = (e.target as HTMLInputElement).value

                if (typeof onChange === 'function') {
                  onChange(
                    newValue,
                    options.find(option => option.value === newValue)
                  )
                }
              }}
              slotProps={{
                select: {
                  multiple,
                  ...selectProps,
                  readOnly: disabledYn,
                  displayEmpty: true,
                  renderValue: (selected: any) => {
                    if (!selected || (Array.isArray(selected) && selected.length === 0)) {
                      return <Typography color='gray'>{placeholder || labelText || __('Select...')}</Typography>
                    }

                    if (multiple && Array.isArray(selected)) {
                      const founds = options.filter(option => selected.includes(option.value))

                      return (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {founds.map(f => (
                            <Chip
                              key={f.value}
                              label={
                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  {f.icon}
                                  {f.label}
                                </span>
                              }
                              onMouseDown={e => e.stopPropagation()}
                              onDelete={() => {
                                const newMultiValue = (currentValue as any[]).filter((id: string) => id !== f.value)

                                if (typeof onChange === 'function') {
                                  onChange(newMultiValue)
                                }

                                setValue(name, newMultiValue)
                              }}
                              size='small'
                              style={{ margin: 2 }}
                            />
                          ))}
                        </div>
                      )
                    }

                    let found = options.find(option => option.value === selected)

                    if (forceLabelFromOptions && !found && selected !== undefined && selected !== null) {
                      const maybeValue = (selected as any).value

                      const selectedValue =
                        maybeValue !== undefined && maybeValue !== null ? maybeValue : (selected as any)

                      found = options.find(option => option.value === selectedValue)
                    }

                    return <>{found ? found.label : selected}</>
                  }
                },
                input: {
                  endAdornment:
                    !!currentValue && typeof onClear === 'function' ? (
                      <InputAdornment position='end'>
                        <IconButton
                          aria-label='clear selection'
                          onClick={() => {
                            onClear?.()
                            setValue(name, multiple ? [] : '')
                          }}
                          edge='end'
                          size='small'
                          tabIndex={-1}
                          sx={{ mr: 5 }}
                        >
                          <i className='tabler-circle-x' />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                },
                ...slotProps
              }}
              label={labelText}
              placeholder={placeholder}
              defaultValue={defaultValue}
              fullWidth
              {...(fieldState.error && { error: true, helperText: fieldState.error.message?.toString() })}
              sx={{
                '& .MuiFormLabel-root': {
                  fontSize: '15px',
                  fontWeight: '400'
                },
                '& .MuiInputLabel-root:not(.Mui-error).MuiFormLabel-colorPrimary.Mui-focused': {
                  color: '#3551f2 !important'
                },
                '& .MuiInputBase-root': {
                  minHeight: height,
                  height: 'auto'
                },
                '& .MuiInputBase-root.Mui-focused': {
                  borderColor: '#3551f2 !important',
                  boxShadow: 'none'
                },
                '& .MuiFormLabel-asterisk': {
                  color: '#FF4C51'
                },
                '& .MuiTypography-root': {
                  fontSize: '15px',
                  fontWeight: '400',
                  color: '#2f2b3d66'
                },
                ...sxProps
              }}
              {...props}
            >
              {options.map((option, index) => (
                <MenuItem key={`${name}-${option.value}-${index}`} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </CustomTextField>
          )
        }}
      />
    </div>
  )
}

export default FormSelect
